import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { Notification } from '../types';

interface WebSocketHookProps {
  token: string | null;
  onNotification?: (notification: Notification) => void;
  onNotificationCountUpdate?: (counts: { unread: number; total: number }) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

export const useWebSocket = ({
  token,
  onNotification,
  onNotificationCountUpdate,
  onConnect,
  onDisconnect
}: WebSocketHookProps) => {
  const socketRef = useRef<Socket | null>(null);
  const isConnectedRef = useRef(false);

  const connect = useCallback(() => {
    if (!token || socketRef.current?.connected) {
      return;
    }

    console.log('Connecting to WebSocket server...');

    const socket = io(process.env.REACT_APP_API_URL || 'http://localhost:4000', {
      auth: {
        token: token
      },
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      timeout: 20000
    });

    socket.on('connect', () => {
      console.log('WebSocket connected');
      isConnectedRef.current = true;
      onConnect?.();
    });

    socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      isConnectedRef.current = false;
      onDisconnect?.();
    });

    socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      isConnectedRef.current = false;
    });

    socket.on('new_notification', (notification: Notification) => {
      console.log('Received new notification:', notification);
      onNotification?.(notification);
    });

    socket.on('notification_count_update', (counts: { unread: number; total: number }) => {
      console.log('Received notification count update:', counts);
      onNotificationCountUpdate?.(counts);
    });

    socketRef.current = socket;
  }, [token, onNotification, onNotificationCountUpdate, onConnect, onDisconnect]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      console.log('Disconnecting from WebSocket server...');
      socketRef.current.disconnect();
      socketRef.current = null;
      isConnectedRef.current = false;
    }
  }, []);

  const acknowledgeNotification = useCallback((notificationId: number) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('notification_received', notificationId);
    }
  }, []);

  // Auto-connect when token is available
  useEffect(() => {
    if (token && !socketRef.current?.connected) {
      connect();
    } else if (!token && socketRef.current) {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [token, connect, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected: isConnectedRef.current,
    connect,
    disconnect,
    acknowledgeNotification
  };
}; 